import { Divider, Flex, Stack, Text } from '@chakra-ui/react';

import { TeamMemberRole } from '__generated__/GQL';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Skeleton from 'components/Skeleton';
import { computeRoleBillingDelta } from 'features/Team/utils';

import useTeamMembersInvitationQuota from '../../hooks/useTeamMembersInvitationQuota';
import locales from './locales';

type InvitationCostReviewProps =
  | {
      showDiff?: false;
      effectMessage?: string;
    }
  | {
      currentRole?: TeamMemberRole;
      newRole: TeamMemberRole;
      showDiff: true;
      effectMessage?: string;
    };

export const InvitationCostReview = (props: InvitationCostReviewProps) => {
  const {
    extraSeatCost,
    invitationsLeft,
    loading: teamMembersInvitationQuotaLoading,
    teamMemberInvitationsQuota: { limit },
  } = useTeamMembersInvitationQuota();

  const roleBillingDelta = props.showDiff
    ? computeRoleBillingDelta(props.newRole, props.currentRole)
    : 0;
  const adjustedInvitationLeft = Math.min(
    invitationsLeft - roleBillingDelta,
    limit,
  );

  return (
    <SunshineCard padding="space-0">
      <Flex justify="space-between" margin="space-16">
        <Text variant="secondary">{locales.seatsLeft}</Text>

        <Skeleton
          endColor="grey.300"
          isLoaded={!teamMembersInvitationQuotaLoading}
          startColor="grey.100"
          width={!teamMembersInvitationQuotaLoading ? 'auto' : '1.5em'}
        >
          <Text variant={invitationsLeft > 0 ? 'primary' : 'light'}>
            {props.showDiff ? (
              <>
                {invitationsLeft} <SunshineIcon name="arrow-right" />{' '}
                {adjustedInvitationLeft}
              </>
            ) : (
              invitationsLeft
            )}
          </Text>
        </Skeleton>
      </Flex>

      <Divider marginY="space-16" />

      <Flex gap="space-24" margin="space-16">
        <Stack flex={1} gap="space-4">
          <Skeleton
            endColor="grey.300"
            isLoaded={!teamMembersInvitationQuotaLoading}
            startColor="grey.100"
            width="95%"
          >
            <Text flex="1" variant="secondary">
              {locales.price.label}
            </Text>
            {adjustedInvitationLeft <= 0 ? (
              <Text size="sm" variant="light">
                {locales.formatString(locales.price.description, {
                  extraSeatCost: extraSeatCost / 100,
                })}
              </Text>
            ) : null}
          </Skeleton>
        </Stack>
        <Skeleton
          endColor="grey.300"
          height={!teamMembersInvitationQuotaLoading ? 'auto' : '2em'}
          isLoaded={!teamMembersInvitationQuotaLoading}
          startColor="grey.100"
          width={!teamMembersInvitationQuotaLoading ? 'auto' : '6em'}
        >
          <Text fontWeight="weight-500" variant="primary">
            {adjustedInvitationLeft > 0
              ? locales.price.included
              : (locales.formatString(locales.price.perMonth, {
                  extraSeatCost: extraSeatCost / 100,
                }) as string)}
          </Text>
        </Skeleton>
      </Flex>
      {props.effectMessage ? (
        <>
          <Divider marginY="space-16" />
          <Flex gap="space-24" margin="space-16">
            <Text flex="1" variant="secondary">
              {props.effectMessage}
            </Text>
          </Flex>
        </>
      ) : null}
    </SunshineCard>
  );
};
